/**
 * End-scene model (R13 U1 — the S9 terminal scene).
 *
 * When the visitor taps Done on the S7 close card, the kiosk sends `close-done {}` and advances to
 * the S9 'end' scene: the gallery-grade **style-snapshot reveal** (Gigi's read of the visitor's
 * aesthetic, from the same preference-graph/style-snapshot data that already exists server-side) +
 * a warm thanks line + a feedback ask ("does this read of your style feel right?").
 *
 * The server (concierge C2) drives the scene with `ui.scene {scene:'end', payload:{…}}` and sends
 * whatever snapshot payload the reveal needs. We parse it TOLERANTLY (the same seam discipline as
 * ui.categories / ui.contact_field): the snapshot facets may arrive at the top of `payload` OR
 * under a nested `snapshot`/`style_snapshot` key, and each facet may be a string or an array. A
 * missing/malformed payload is fine — the reveal falls back to a graceful "captured from our chat"
 * message (the thanks + feedback framing never depends on the payload).
 *
 * Pure (no React / no DOM) so parsing is unit-testable.
 */

/** The visitor's style read, normalised for the end-scene reveal. All facets optional. */
export type StyleSnapshot = {
  /** Aesthetic styles Gigi inferred ("Contemporary", "Coastal"). */
  styles: string[];
  /** Finish / texture preferences ("Hammered", "Antique"). */
  finishes: string[];
  /**
   * Materials Gigi inferred ("brass", "iron", "marble"). R15 T3 — §12.4's `style_snapshot` carries a
   * `materials` facet the end scene now surfaces (pre-selected chip + palette), so the visitor can
   * confirm/correct the material read too, not just style/finish/tone.
   */
  materials: string[];
  /** Colour tones ("Warm neutrals", "Gold tones"). */
  colourTones: string[];
  /** A short free-text read Gigi composed, if the server sent one. */
  summary?: string;
};

/** True if a snapshot carries any facet worth rendering as the reveal (else show the fallback copy). */
export function hasSnapshotContent(s: StyleSnapshot): boolean {
  return (
    s.styles.length > 0 ||
    s.finishes.length > 0 ||
    s.materials.length > 0 ||
    s.colourTones.length > 0 ||
    (s.summary?.trim() ?? '') !== ''
  );
}

/** Coerce an unknown value into a clean string[] (accepts a bare string, an array, or nothing). */
function toStringArray(v: unknown): string[] {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? [] : [t];
  }
  if (Array.isArray(v)) {
    return v
      .filter((x): x is string => typeof x === 'string' && x.trim() !== '')
      .map((x) => x.trim());
  }
  return [];
}

type RawSnapshot = {
  styles?: unknown;
  style?: unknown; // singular alias
  finishes?: unknown;
  finish?: unknown;
  materials?: unknown;
  material?: unknown; // singular alias
  colour_tones?: unknown;
  colourTones?: unknown;
  colour_tone?: unknown;
  colourTone?: unknown; // camel singular alias
  summary?: unknown;
  read?: unknown; // alias for a free-text read
};

/**
 * Parse a `ui.scene {scene:'end'}` payload into a StyleSnapshot. Tolerant of nesting (the facets may
 * be at the top of `payload` or under `payload.snapshot` / `payload.style_snapshot`) and of
 * singular/plural + snake/camel facet keys. Always returns a StyleSnapshot (never null) — the end
 * scene renders even with an empty payload (thanks + feedback don't depend on the snapshot).
 */
export function parseEndScenePayload(payload: unknown): StyleSnapshot {
  const empty: StyleSnapshot = { styles: [], finishes: [], materials: [], colourTones: [] };
  if (!payload || typeof payload !== 'object') return empty;
  const outer = payload as {
    payload?: unknown;
    snapshot?: unknown;
    style_snapshot?: unknown;
  } & RawSnapshot;
  // Unwrap a nested payload / snapshot wrapper if present; otherwise read the object itself. R18 X3
  // — when the facets sit UNDER a `payload.style_snapshot` double-wrap (`{payload:{style_snapshot}}`),
  // step into `payload` then continue into its `style_snapshot`/`snapshot`, so the real
  // `ui.scene {end, payload:{style_snapshot}}` shape binds whether setEndScenePayload was handed
  // `m.payload` (single wrap) OR the whole `m` (double wrap).
  const unwrapOnce = (o: unknown): object | null => {
    if (!o || typeof o !== 'object') return null;
    const c = o as { snapshot?: unknown; style_snapshot?: unknown; payload?: unknown };
    if (c.snapshot && typeof c.snapshot === 'object') return c.snapshot as object;
    if (c.style_snapshot && typeof c.style_snapshot === 'object') return c.style_snapshot as object;
    if (c.payload && typeof c.payload === 'object') return c.payload as object;
    return null;
  };
  let nestedSource = unwrapOnce(outer);
  const deeper = nestedSource ? unwrapOnce(nestedSource) : null;
  if (deeper) nestedSource = deeper;
  const src = (nestedSource ?? outer) as RawSnapshot;
  const styles = [...toStringArray(src.styles), ...toStringArray(src.style)];
  const finishes = [...toStringArray(src.finishes), ...toStringArray(src.finish)];
  const materials = [...toStringArray(src.materials), ...toStringArray(src.material)];
  const colourTones = [
    ...toStringArray(src.colour_tones),
    ...toStringArray(src.colourTones),
    ...toStringArray(src.colour_tone),
    ...toStringArray(src.colourTone),
  ];
  const summaryRaw = src.summary ?? src.read;
  const summary =
    typeof summaryRaw === 'string' && summaryRaw.trim() !== '' ? summaryRaw.trim() : undefined;
  return {
    styles: dedupe(styles),
    finishes: dedupe(finishes),
    materials: dedupe(materials),
    colourTones: dedupe(colourTones),
    summary,
  };
}

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr) {
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/**
 * R18 X2 — the SINGLE end-scene confirm affordance's presentation state. The R16 V3 change
 * accidentally rendered TWO confirm buttons (a primary AND a ghost that BOTH read "Looks right" when
 * nothing was toggled — a filled + ghost duplicate side-by-side, both calling confirm()). The end
 * scene now renders exactly one button whose label + prominence this pure helper describes, so the
 * single-button invariant + the label matrix are unit-testable without a DOM.
 *
 *   - `prominent` → the accent + brass ring-pulse treatment (btn-accent confirm-attention): true iff
 *     the visitor has TOUCHED a chip (`dirty`), so they know edits persist only on Confirm.
 *   - `label` → "Confirm my changes" when dirty; else "Confirm my style" when chips are pre-selected;
 *     else "Looks right" (confirm the read as-is). Never two buttons, never two "Looks right".
 */
export type ConfirmButtonState = { label: string; prominent: boolean };

export function endConfirmButtonState(dirty: boolean, totalSelected: number): ConfirmButtonState {
  if (dirty) return { label: 'Confirm my changes', prominent: true };
  if (totalSelected > 0) return { label: 'Confirm my style', prominent: false };
  return { label: 'Looks right', prominent: false };
}

/**
 * R29 X3.2 — a one-line "what we learned" recap, built ONLY from the snapshot the server ALREADY
 * sends (no new field): the server's free-text `summary`/`read` when present, else a terse join of
 * the captured facet values (styles · finishes · materials · tones, in that reading order, capped so
 * the line stays a single beat). Returns null when there's nothing worth reciting — the recap card is
 * then not rendered (grounding law: we only read back what was captured). Pure / unit-testable.
 */
export function styleRecapLine(s: StyleSnapshot, maxTerms = 4): string | null {
  const summary = s.summary?.trim();
  if (summary) return summary;
  const terms = [...s.styles, ...s.finishes, ...s.materials, ...s.colourTones]
    .map((t) => t.trim())
    .filter((t) => t !== '');
  if (terms.length === 0) return null;
  const shown = terms.slice(0, maxTerms);
  const line = shown.join(' · ');
  return terms.length > maxTerms ? `${line} …` : line;
}
